import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable, Subject, catchError, of, take } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { AccountService } from '../../../account/account.service';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { createFormDirtyTracker } from '../../../shared/forms/dirty-tracker';
import { requiredNotBlank } from '../../../shared/forms/required-not-blank.validator';
import { isStaleVersion, serverMessage } from '../../../shared/http/server-message';
import { nextColumnName, toGridState, toSaveRequest } from './testdatenblatt-form.mappers';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { findRouteParam } from '../../../shared/route-params';
import { SecretService, isEncryptedValue } from '../../../services/secret.service';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { ProjektArchiviertHinweisComponent } from '../../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';
import { TestdatenblattDetailsHistorieComponent } from '../testdatenblatt-details-historie/testdatenblatt-details-historie.component';
import { TestdatenblattUsageComponent } from '../testdatenblatt-usage/testdatenblatt-usage.component';

/** Fixed number of dots, so the display does not reveal the length (NEX-1878). */
const ENCRYPTED_DISPLAY = '🔒 ••••••••';

interface CellReentry {
  readonly r: number;
  readonly c: number;
  readonly previous: string;
}

@Component({
  selector: 'app-testdatenblatt-form',
  imports: [
    ProjektArchiviertHinweisComponent,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    MatButton,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInput,
    MatProgressSpinner,
    MatTooltip,
    SectionTitleComponent,
    PersonSelectComponent,
    Modal,
    ModalEscapeGuardDirective,
    TestdatenblattDetailsHistorieComponent,
    TestdatenblattUsageComponent,
  ],
  templateUrl: './testdatenblatt-form.component.html',
  styleUrl: './testdatenblatt-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestdatenblattFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(TestdatenblattService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);
  private readonly secretService = inject(SecretService);
  private readonly injector = inject(Injector);
  private readonly saveToast = inject(SaveToastService);

  private readonly addColBtn = viewChild<ElementRef<HTMLButtonElement>>('addColBtn');
  private readonly addRowBtn = viewChild<ElementRef<HTMLButtonElement>>('addRowBtn');
  private readonly colDelBtns = viewChildren<ElementRef<HTMLButtonElement>>('colDel');
  private readonly rowDelBtns = viewChildren<ElementRef<HTMLButtonElement>>('rowDel');
  private readonly detailsHistorie = viewChild(TestdatenblattDetailsHistorieComponent);

  readonly isReadOnly = this.projectRoleService.isReadOnly;
  readonly isProjectReader = this.projectRoleService.isProjectReader;
  readonly isArchived = this.projectRoleService.isArchived;

  readonly projectId: string;
  tdbId: string | null;
  isEdit: boolean;
  readonly currentTdbId = signal<string | null>(null);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly notFound = signal(false);
  private readonly sheetLoaded = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorText = signal<string | null>(null);
  readonly saveNotFound = signal(false);

  /** Column names (= spalten). */
  readonly cols = signal<string[]>(['variable_1']);
  /** Cell values as parallel arrays (row = values in column order). */
  readonly rows = signal<string[][]>([['']]);
  /** On/off switch per row, parallel to {@link rows} (NEX-2264). */
  readonly active = signal<boolean[]>([true]);
  readonly activeCount = computed(() => this.active().filter(Boolean).length);
  private readonly hasBlankColumnName = computed(() => this.cols().some((name) => name.trim() === ''));

  readonly dataVariablesKey = computed(() =>
    this.cols().length === 1
      ? 'testdatenblaetter.form.section.dataVariablesOne'
      : 'testdatenblaetter.form.section.dataVariablesOther',
  );
  readonly dataRecordsKey = computed(() =>
    this.rows().length === 1
      ? 'testdatenblaetter.form.section.dataRecordsOne'
      : 'testdatenblaetter.form.section.dataRecordsOther',
  );

  readonly fieldLengths = FIELD_LENGTHS;
  readonly tagSeparatorKeys = [ENTER, COMMA] as const;
  readonly encryptedDisplay = ENCRYPTED_DISPLAY;
  readonly isEncrypted = isEncryptedValue;

  /** Key `r-c` of the cell being encrypted right now, and of the one whose last attempt failed. */
  readonly encryptingCell = signal<string | null>(null);
  readonly failedCell = signal<string | null>(null);
  /** An encrypted cell the user is typing a new value into; Esc or leaving it empty brings `previous` back. */
  private reentry: CellReentry | null = null;
  /** Key des geladenen Datenblatts (TDB-###, NEX-654); leer bis geladen. */
  readonly tdbKey = signal('');

  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));

  readonly form = this.fb.group({
    name: ['', [requiredNotBlank, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
    assigneeUserId: ['', Validators.required],
    beschreibung: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    tags: [[] as string[]],
    version: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    notes: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
  });

  /**
   * NEX-824: cached current-user id, resolved once regardless of edit/create —
   * the component is reused across route param changes (route reuse), so
   * `applyAssigneeDefaultIfCreating` re-checks `isEdit` at call time rather than
   * refetching the profile on every navigation into create mode.
   */
  readonly currentUserId = signal<string | null>(null);

  readonly notesText = toSignal(this.form.controls.notes.valueChanges, { initialValue: '' });

  readonly tdbLabel = computed(() => this.tdbKey());

  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({ cols: this.cols(), rows: this.rows(), active: this.active() }),
  });

  saveDisabled(): boolean {
    if (this.isEdit && !this.sheetLoaded()) return true;
    if (this.saveNotFound() || this.hasBlankColumnName()) return true;
    return this.saving() || this.form.invalid || (this.isEdit && !this.form.dirty);
  }

  isBlankColumnName(name: string): boolean {
    return !this.isReadOnly() && name.trim() === '';
  }

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.requireParam('projectId');
    this.tdbId = this.findParam('tdbId');
    this.isEdit = this.tdbId !== null;

    effect(() => {
      if (this.isReadOnly()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadCurrentUserId();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncDirty());
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.initFor(params.get('tdbId')));
  }

  private loadUsers(): void {
    // Der Verantwortlicher-Dropdown listet nur aktive Projekt-Mitglieder.
    // Fremde Tenant-Nutzer sind nicht wählbar, da sie das Testdatenblatt nicht sähen.
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('testdatenblaetter.form.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => {
        this.users.set(members);
        this.applyAssigneeDefaultIfCreating();
      });
  }

  private loadCurrentUserId(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        this.currentUserId.set(profile?.id ?? null);
        this.applyAssigneeDefaultIfCreating();
      });
  }

  /**
   * NEX-824: prefills the assignee with the current user when creating a new
   * sheet. Called from both loading points (users, profile) plus whenever a
   * create route is (re-)entered, since any of them may resolve last — the
   * underlying helper is idempotent (no-op once touched or already filled).
   */
  private applyAssigneeDefaultIfCreating(): void {
    if (this.isEdit) return;
    const userId = this.currentUserId();
    if (!userId) return;
    prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, userId, this.personOptions());
    this.snapshot();
  }

  private initFor(tdbId: string | null): void {
    this.tdbId = tdbId;
    this.isEdit = tdbId !== null;
    this.currentTdbId.set(tdbId);
    this.saveError.set(false);
    this.saveErrorText.set(null);
    this.saveNotFound.set(false);
    if (tdbId) {
      this.load(tdbId);
      return;
    }
    this.form.reset();
    this.cols.set(['variable_1']);
    this.rows.set([['']]);
    this.active.set([true]);
    this.applyAssigneeDefaultIfCreating();
    this.snapshot();
  }

  // ── Spreadsheet operations ────────────────────────────────────────────
  setCell(r: number, c: number, value: string): void {
    this.rows.update((rows) =>
      rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : row)),
    );
    this.syncDirty();
  }

  renameCol(c: number, value: string): void {
    this.cols.update((cols) => cols.map((x, ci) => (ci === c ? value : x)));
    this.syncDirty();
  }

  addCol(): void {
    this.cols.update((cols) => [...cols, nextColumnName(cols)]);
    this.rows.update((rows) => rows.map((row) => [...row, '']));
    this.syncDirty();
  }

  delCol(c: number): void {
    if (this.cols().length <= 1) return;
    this.cols.update((cols) => cols.filter((_, ci) => ci !== c));
    this.rows.update((rows) => rows.map((row) => row.filter((_, ci) => ci !== c)));
    this.syncDirty();
    this.focusAfterDelete(c, this.colDelBtns, this.addColBtn);
  }

  addRow(): void {
    this.rows.update((rows) => [...rows, this.cols().map(() => '')]);
    this.active.update((active) => [...active, true]);
    this.syncDirty();
  }

  delRow(r: number): void {
    if (this.rows().length <= 1) return;
    this.rows.update((rows) => rows.filter((_, ri) => ri !== r));
    this.active.update((active) => active.filter((_, ri) => ri !== r));
    this.syncDirty();
    this.focusAfterDelete(r, this.rowDelBtns, this.addRowBtn);
  }

  toggleRowActive(r: number): void {
    this.active.update((active) => active.map((on, ri) => (ri === r ? !on : on)));
    this.syncDirty();
  }

  private focusAfterDelete(
    index: number,
    deleteButtons: () => readonly ElementRef<HTMLButtonElement>[],
    addButton: () => ElementRef<HTMLButtonElement> | undefined,
  ): void {
    afterNextRender(
      () => {
        const buttons = deleteButtons();
        const target = buttons.length <= 1 ? addButton() : buttons[Math.min(index, buttons.length - 1)];
        target?.nativeElement.focus();
      },
      { injector: this.injector },
    );
  }

  // ── Encrypted cells (NEX-1878) ────────────────────────────────────────
  cellKey(r: number, c: number): string {
    return `${r}-${c}`;
  }

  startReentry(r: number, c: number): void {
    const previous = this.rows()[r]?.[c] ?? '';
    if (this.isReadOnly() || !isEncryptedValue(previous)) return;
    this.reentry = { r, c, previous };
    this.setCell(r, c, '');
  }

  cancelReentry(r: number, c: number): void {
    const reentry = this.reentryAt(r, c);
    if (!reentry) return;
    this.reentry = null;
    this.setCell(r, c, reentry.previous);
  }

  endReentry(r: number, c: number): void {
    const reentry = this.reentryAt(r, c);
    if (!reentry) return;
    this.reentry = null;
    if (this.rows()[r]?.[c] === '') this.setCell(r, c, reentry.previous);
  }

  encryptCell(r: number, c: number): void {
    const value = this.rows()[r]?.[c] ?? '';
    if (!value || this.isReadOnly() || this.encryptingCell() !== null) return;
    const key = this.cellKey(r, c);
    this.encryptingCell.set(key);
    this.failedCell.set(null);
    this.secretService
      .encrypt(this.projectId, value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (encrypted) => {
          this.encryptingCell.set(null);
          // The grid may have changed while the request ran; never overwrite another value.
          if (this.rows()[r]?.[c] === value) this.setCell(r, c, encrypted);
        },
        error: () => {
          this.encryptingCell.set(null);
          this.failedCell.set(key);
        },
      });
  }

  private reentryAt(r: number, c: number): CellReentry | null {
    const reentry = this.reentry;
    return reentry && reentry.r === r && reentry.c === c ? reentry : null;
  }

  // ── Tags (NEX-2272) ───────────────────────────────────────────────────
  /** Case stays as typed: imported tags like `attr:Status=Freigegeben` must keep it. */
  addTag(event: MatChipInputEvent): void {
    if (this.isReadOnly()) return;
    const value = (event.value ?? '').trim();
    const tags = this.form.controls.tags;
    if (value && !tags.value.includes(value)) {
      tags.setValue([...tags.value, value]);
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    if (this.isReadOnly()) return;
    const tags = this.form.controls.tags;
    tags.setValue(tags.value.filter((t) => t !== tag));
  }

  // ── Save / cancel ─────────────────────────────────────────────────────
  onSave(): void {
    if (this.saveNotFound() || this.hasBlankColumnName()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving()) return;
    this.saving.set(true);
    this.saveError.set(false);
    this.saveErrorText.set(null);

    const raw = this.form.getRawValue();
    const request = toSaveRequest(
      raw.name,
      raw.beschreibung,
      raw.assigneeUserId,
      this.cols(),
      this.rows(),
      this.active(),
      raw.tags,
      { version: raw.version, notes: raw.notes },
    );

    const request$ =
      this.isEdit && this.tdbId
        ? this.service.update(this.projectId, this.tdbId, request)
        : this.service.create(this.projectId, { ...request, testModuleIds: [] });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (sheet) => {
        this.saving.set(false);
        this.tdbKey.set(sheet.key);
        if (this.isEdit) {
          this.snapshot();
          this.detailsHistorie()?.ladeVerlauf();
          this.saveToast.show(this.translateService.instant('testdatenblaetter.form.savedToast'));
          return;
        }
        // NEX-814: Dirty-Zustand vor der Weiterleitung zurücksetzen, sonst sieht der
        // CanDeactivate-Guard das noch dirty-markierte Formular und wirft beim Anlegen
        // fälschlich die Verlassen-Modal (statt zu speichern).
        this.snapshot();
        this.saveToast.show(this.translateService.instant('testdatenblaetter.form.createdToast'));
        this.router.navigate(['/projects', this.projectId, 'testdatenblaetter', sheet.id, 'bearbeiten']);
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.saveError.set(true);
        this.saveNotFound.set(this.isEdit && err instanceof HttpErrorResponse && err.status === 404);
        this.saveErrorText.set(isStaleVersion(err) ? null : serverMessage(err));
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/projects', this.projectId, 'testdatenblaetter']);
  }

  hasUnsavedChanges(): boolean {
    return (this.form.dirty || this.uebergabeEntwurfUngesendet()) && !this.saving();
  }

  private uebergabeEntwurfUngesendet(): boolean {
    const entwurf = this.detailsHistorie()?.entwurf();
    return !!entwurf && (!!entwurf.userId || !!entwurf.nachricht.trim());
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  // ── Loading ───────────────────────────────────────────────────────────
  private load(id: string): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.notFound.set(false);
    this.sheetLoaded.set(false);
    this.service
      .getById(this.projectId, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sheet) => {
          this.apply(sheet);
          this.sheetLoaded.set(true);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.notFound.set(true);
          } else {
            this.loadError.set(true);
          }
          this.loading.set(false);
        },
      });
  }

  private apply(sheet: Testdatenblatt): void {
    this.tdbKey.set(sheet.key);
    this.form.patchValue({
      name: sheet.name,
      assigneeUserId: sheet.assigneeUserId ?? '',
      beschreibung: sheet.beschreibung ?? '',
      tags: sheet.tags ?? [],
      version: sheet.version ?? '',
      notes: sheet.notes ?? '',
    });
    const grid = toGridState(sheet);
    this.cols.set(grid.cols);
    this.rows.set(grid.rows);
    this.active.set(grid.active);
    this.form.markAsPristine();
    this.snapshot();
  }

  private snapshot(): void {
    this.dirtyTracker.reset();
    this.form.markAsPristine();
  }

  private syncDirty(): void {
    if (this.dirtyTracker.isDirtyNow()) {
      this.form.markAsDirty();
    } else {
      this.form.markAsPristine();
    }
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
